import { api, Meetup } from '../services/api';
import {
  ensureMeetupInSystemCalendar,
  removeMeetupFromSystemCalendar,
  restoreMeetupTimeInSystemCalendar,
  restoreMeetupInSystemCalendar,
  systemCalendarAvailable,
} from './systemCalendar';
import { buildSystemCalendarDraft } from './systemCalendarTypes';
import { isDeviceCalendarConnectionEnabled } from './calendarConnection';

type CalendarLabels = {
  meetupTitle: (name: string) => string;
  callTitle: (name: string) => string;
  notes: string;
};

export async function syncAcceptedMeetup(
  userId: number,
  meetup: Meetup,
  labels: CalendarLabels
) {
  if (!systemCalendarAvailable || meetup.status !== 'accepted') return 'unavailable' as const;
  if (!(await isDeviceCalendarConnectionEnabled(userId))) return 'unavailable' as const;
  const endAt = new Date(meetup.proposed_at).getTime() + Math.max(1, meetup.duration_minutes) * 60_000;
  if (!Number.isFinite(endAt) || endAt <= Date.now()) return 'existing' as const;

  return ensureMeetupInSystemCalendar(userId, meetup.id, buildSystemCalendarDraft({
    title: meetup.kind === 'call'
      ? labels.callTitle(meetup.other_name)
      : labels.meetupTitle(meetup.other_name),
    proposedAt: meetup.proposed_at,
    durationMinutes: meetup.duration_minutes,
    location: meetup.location,
    notes: labels.notes,
    timeZone: meetup.source_time_zone,
    scheduleRevision: meetup.schedule_revision ?? 0,
  }));
}

export async function restoreAcceptedMeetup(
  userId: number,
  meetup: Meetup,
  labels: CalendarLabels
) {
  if (!(await isDeviceCalendarConnectionEnabled(userId))) return 'unavailable' as const;
  return restoreMeetupInSystemCalendar(userId, meetup.id, buildSystemCalendarDraft({
    title: meetup.kind === 'call'
      ? labels.callTitle(meetup.other_name)
      : labels.meetupTitle(meetup.other_name),
    proposedAt: meetup.proposed_at,
    durationMinutes: meetup.duration_minutes,
    location: meetup.location,
    notes: labels.notes,
    timeZone: meetup.source_time_zone,
    scheduleRevision: meetup.schedule_revision ?? 0,
  }));
}

export async function restoreAcceptedMeetupTime(
  userId: number,
  meetup: Meetup,
  labels: CalendarLabels
) {
  if (!(await isDeviceCalendarConnectionEnabled(userId))) return 'unavailable' as const;
  return restoreMeetupTimeInSystemCalendar(userId, meetup.id, buildSystemCalendarDraft({
    title: meetup.kind === 'call'
      ? labels.callTitle(meetup.other_name)
      : labels.meetupTitle(meetup.other_name),
    proposedAt: meetup.proposed_at,
    durationMinutes: meetup.duration_minutes,
    location: meetup.location,
    notes: labels.notes,
    timeZone: meetup.source_time_zone,
    scheduleRevision: meetup.schedule_revision ?? 0,
  }));
}

export async function syncMeetupsToSystemCalendar(
  token: string,
  userId: number,
  labels: CalendarLabels
) {
  if (!systemCalendarAvailable) return;
  if (!(await isDeviceCalendarConnectionEnabled(userId))) return;
  const meetups = await api.getMeetups(token);
  await Promise.allSettled(meetups.map((meetup) => {
    if (meetup.status === 'accepted') return syncAcceptedMeetup(userId, meetup, labels);
    if (meetup.status === 'cancelled') {
      return removeMeetupFromSystemCalendar(userId, meetup.id);
    }
    return Promise.resolve('missing' as const);
  }));
}
